import { ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export type HeroImage = { src: string; alt?: string; video?: boolean; poster?: string };

export type HeroCta = {
  label: string;
  href: string;
  external?: boolean;
  variant?: "primary" | "secondary";
};

export type HeroBannerProps = {
  /** Small badge / kicker above the headline */
  kicker?: ReactNode;
  /** Main headline — plain text or JSX */
  title: ReactNode;
  /** Optional highlighted portion of the title (rendered in a stronger weight) */
  titleHighlight?: ReactNode;
  /** Subtitle / description paragraph */
  subtitle: ReactNode;
  /** Primary CTA button */
  primaryCta?: HeroCta;
  /** Secondary CTA button */
  secondaryCta?: HeroCta;
  /** Optional single hero image URL (legacy) */
  image?: string;
  /** Alt text for the hero image */
  imageAlt?: string;
  /** Optional list of images for a scrolling carousel (takes precedence over `image`) */
  images?: HeroImage[];
  /** Auto-advance interval in ms (default 5000) */
  autoplayMs?: number;
  /** Small check-list items shown under the CTAs */
  checks?: string[];
  /** Reversed layout (image left, text right) */
  reversed?: boolean;
  /** Extra content rendered under the checks / CTAs area */
  children?: ReactNode;
};

const prefersReduced =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function Reveal({
  children,
  delay = 0,
  direction = "up",
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  direction?: "up" | "left" | "right";
  className?: string;
}) {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.1);

  const transformHidden =
    direction === "up"
      ? "translateY(24px)"
      : direction === "left"
        ? "translateX(-32px)"
        : "translateX(32px)";

  return (
    <div
      ref={ref}
      className={className}
      style={
        prefersReduced
          ? {}
          : {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translate(0)" : transformHidden,
              transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
              willChange: "opacity, transform",
            }
      }
    >
      {children}
    </div>
  );
}

function CtaButton({ cta }: { cta: HeroCta }) {
  const isPrimary = cta.variant !== "secondary";
  const base =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 text-base font-bold transition-all hover:-translate-y-0.5";
  const primary =
    "bg-primary text-primary-foreground shadow-elegant";
  const secondary =
    "border border-border bg-card text-foreground hover:bg-primary hover:text-primary-foreground";
  const classes = `${base} ${isPrimary ? primary : secondary}`;

  const content = (
    <>
      {isPrimary && <MessageCircle className="h-5 w-5" />}
      {cta.label}
      {isPrimary && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
    </>
  );

  if (cta.external) {
    return (
      <a
        href={cta.href}
        target="_blank"
        rel="noopener noreferrer"
        className={`group ${classes}`}
      >
        {content}
      </a>
    );
  }
  return (
    <a href={cta.href} className={`group ${classes}`}>
      {content}
    </a>
  );
}

export function HeroBanner({
  kicker,
  title,
  titleHighlight,
  subtitle,
  primaryCta,
  secondaryCta,
  image,
  imageAlt,
  images,
  autoplayMs = 5000,
  checks,
  reversed = false,
  children,
}: HeroBannerProps) {
  const imageDir = reversed ? "left" : "right";

  // Normalize to a slides list. `images` wins; otherwise fall back to single `image`.
  const slides: HeroImage[] =
    images && images.length > 0
      ? images
      : image
        ? [{ src: image, alt: imageAlt }]
        : [];

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length < 2 || prefersReduced) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, autoplayMs);
    return () => window.clearInterval(id);
  }, [slides.length, autoplayMs]);

  return (
    <section id="top" className="relative overflow-hidden bg-background">
      <div
        aria-hidden
        className="absolute -top-40 -right-40 h-[36rem] w-[36rem] rounded-full bg-accent/10 blur-[160px]"
      />
      <div
        className={`relative mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 sm:gap-12 sm:px-6 sm:py-20 lg:grid-cols-12 lg:gap-12 lg:py-24 ${
          reversed ? "lg:direction-rtl" : ""
        }`}
      >
        <div
          className={`lg:col-span-6 ${reversed ? "lg:order-2 lg:direction-ltr" : ""}`}
        >
          {kicker && (
            <Reveal delay={0}>
              <span className="inline-flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-2 text-[10px] font-bold uppercase tracking-[0.25em] text-primary shadow-soft">
                {kicker}
              </span>
            </Reveal>
          )}

          <Reveal delay={90}>
            <h1 className="mt-6 text-[2rem] font-light leading-[1.05] tracking-tight text-foreground sm:mt-8 sm:text-5xl lg:text-6xl xl:text-[4.25rem]">
              {title}
              {titleHighlight && (
                <>
                  <br />
                  <span className="font-semibold">{titleHighlight}</span>
                </>
              )}
            </h1>
          </Reveal>

          <Reveal delay={180}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {subtitle}
            </p>
          </Reveal>

          {(primaryCta || secondaryCta) && (
            <Reveal delay={270}>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                {primaryCta && <CtaButton cta={primaryCta} />}
                {secondaryCta && <CtaButton cta={secondaryCta} />}
              </div>
            </Reveal>
          )}

          {checks && checks.length > 0 && (
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground sm:text-sm">
              {checks.map((c, i) => (
                <Reveal key={c} delay={360 + i * 70}>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    {c}
                  </div>
                </Reveal>
              ))}
            </div>
          )}

          {children}
        </div>

        {slides.length > 0 && (
          <Reveal
            delay={120}
            direction={imageDir}
            className={`relative lg:col-span-6 ${
              reversed ? "lg:order-1" : ""
            }`}
          >
            <div
              aria-hidden
              className="absolute -inset-1 rounded-[2rem] bg-gradient-to-tr from-accent/30 to-primary/10 opacity-60 blur-2xl"
            />
            <div className="relative overflow-hidden rounded-[1.5rem] border border-border bg-card p-2 shadow-elegant sm:p-3">
              {/* Standard professional 16:10 frame, responsive */}
              <div className="relative aspect-[16/10] w-full overflow-hidden rounded-[1.2rem] bg-secondary">
                <div
                  className="flex h-full w-full transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                  style={{ transform: `translateX(-${index * 100}%)` }}
                >
                  {slides.map((s, i) =>
                    s.video ? (
                      <video
                        key={s.src}
                        src={s.src}
                        poster={s.poster}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        aria-label={s.alt ?? ""}
                        className="h-full w-full flex-shrink-0 object-cover"
                      />
                    ) : (
                      <img
                        key={s.src}
                        src={s.src}
                        alt={s.alt ?? ""}
                        width={1280}
                        height={800}
                        loading={i === 0 ? "eager" : "lazy"}
                        decoding="async"
                        className="h-full w-full flex-shrink-0 object-cover"
                        draggable={false}
                      />
                    )
                  )}
                </div>
              </div>

              {slides.length > 1 && (
                <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-background/70 px-3 py-1.5 backdrop-blur">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      aria-label={`Show slide ${i + 1}`}
                      aria-current={i === index}
                      onClick={() => setIndex(i)}
                      className={`h-1.5 rounded-full transition-all ${
                        i === index
                          ? "w-6 bg-primary"
                          : "w-1.5 bg-muted-foreground/50 hover:bg-muted-foreground"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}

